import { useState, useEffect, useCallback } from 'react';

/**
 * 网络状态监控Hook
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionType, setConnectionType] = useState('unknown');
  const [effectiveType, setEffectiveType] = useState('4g');
  const [saveData, setSaveData] = useState(false);
  const [rtt, setRtt] = useState(0);
  const [downlink, setDownlink] = useState(0);

  // 更新网络信息
  const updateNetworkInfo = useCallback(() => {
    if (navigator.connection) {
      const connection = navigator.connection;
      setConnectionType(connection.type || 'unknown');
      setEffectiveType(connection.effectiveType || '4g');
      setSaveData(connection.saveData || false);
      setRtt(connection.rtt || 0);
      setDownlink(connection.downlink || 0);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      updateNetworkInfo();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    const handleConnectionChange = () => {
      updateNetworkInfo();
    };

    // 初始化网络信息
    updateNetworkInfo();

    // 监听网络状态变化
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // 监听连接信息变化
    if (navigator.connection) {
      navigator.connection.addEventListener('change', handleConnectionChange);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if (navigator.connection) {
        navigator.connection.removeEventListener('change', handleConnectionChange);
      }
    };
  }, [updateNetworkInfo]);

  // 网络质量评估
  const getNetworkQuality = useCallback(() => {
    if (!isOnline) return 'offline';
    
    if (effectiveType === 'slow-2g' || effectiveType === '2g') return 'poor';
    if (effectiveType === '3g') return 'moderate';
    if (effectiveType === '4g') return 'good';
    
    return 'unknown';
  }, [isOnline, effectiveType]);

  // 是否为慢速连接
  const isSlowConnection = useCallback(() => {
    return ['slow-2g', '2g'].includes(effectiveType) || saveData;
  }, [effectiveType, saveData]);

  // 是否应该优化资源加载
  const shouldOptimizeLoading = useCallback(() => {
    return !isOnline || isSlowConnection() || saveData;
  }, [isOnline, isSlowConnection, saveData]);

  return {
    isOnline,
    connectionType,
    effectiveType,
    saveData,
    rtt,
    downlink,
    networkQuality: getNetworkQuality(),
    isSlowConnection: isSlowConnection(),
    shouldOptimizeLoading: shouldOptimizeLoading()
  };
};

/**
 * 网络状态通知Hook
 */
export const useNetworkNotification = () => {
  const { isOnline, isSlowConnection } = useNetworkStatus();
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState('offline');

  useEffect(() => {
    if (!isOnline) {
      setNotificationType('offline');
      setShowNotification(true);
    } else if (isSlowConnection) {
      setNotificationType('slow');
      setShowNotification(true);
      // 慢速连接提示3秒后自动隐藏
      const timer = setTimeout(() => setShowNotification(false), 3000);
      return () => clearTimeout(timer);
    } else {
      setShowNotification(false);
    }
  }, [isOnline, isSlowConnection]);

  const hideNotification = useCallback(() => {
    setShowNotification(false);
  }, []);

  return {
    showNotification,
    notificationType,
    hideNotification
  };
};

/**
 * 网络重试Hook
 */
export const useNetworkRetry = (retryFn, maxRetries = 3) => {
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const { isOnline } = useNetworkStatus();

  const retry = useCallback(async () => {
    if (!isOnline || retryCount >= maxRetries || isRetrying) return false;

    setIsRetrying(true);
    
    try {
      await retryFn();
      setRetryCount(0); // 成功后重置计数
      return true;
    } catch (error) {
      setRetryCount(prev => prev + 1);
      return false;
    } finally {
      setIsRetrying(false);
    }
  }, [isOnline, retryCount, maxRetries, isRetrying, retryFn]);

  const reset = useCallback(() => {
    setRetryCount(0);
    setIsRetrying(false);
  }, []);

  return {
    retry,
    reset,
    retryCount,
    isRetrying,
    canRetry: isOnline && retryCount < maxRetries && !isRetrying
  };
};

/**
 * 自适应加载Hook - 根据网络状况调整加载策略
 */
export const useAdaptiveLoading = () => {
  const { shouldOptimizeLoading, effectiveType, saveData } = useNetworkStatus();

  // 获取图片质量设置
  const getImageQuality = useCallback(() => {
    if (saveData) return 'low';
    if (effectiveType === 'slow-2g' || effectiveType === '2g') return 'low';
    if (effectiveType === '3g') return 'medium';
    return 'high';
  }, [effectiveType, saveData]);

  // 获取视频质量设置
  const getVideoQuality = useCallback(() => {
    if (saveData) return '480p';
    if (effectiveType === 'slow-2g' || effectiveType === '2g') return '480p';
    if (effectiveType === '3g') return '720p';
    return '1080p';
  }, [effectiveType, saveData]);

  // 是否应该预加载资源
  const shouldPreload = useCallback(() => {
    return !shouldOptimizeLoading;
  }, [shouldOptimizeLoading]);

  // 获取并发请求数限制
  const getConcurrencyLimit = useCallback(() => {
    if (saveData) return 2;
    if (effectiveType === 'slow-2g' || effectiveType === '2g') return 2;
    if (effectiveType === '3g') return 4;
    return 6;
  }, [effectiveType, saveData]);

  return {
    shouldOptimizeLoading,
    imageQuality: getImageQuality(),
    videoQuality: getVideoQuality(),
    shouldPreload: shouldPreload(),
    concurrencyLimit: getConcurrencyLimit()
  };
};

export default useNetworkStatus;
